import { useCallback, useMemo, useRef, useState } from "react";
import AnchoredPanel from "../../../GlobalComponents/AnchoredPanel/AnchoredPanel";
import { ESG_PILLARS, normalizeTagText } from "../../../../_constants/esgPillars";

const UNUSED_SECTION_TITLE = "Not at the top of any KPI's tags yet";

/**
 * The listing split by the tag group each module tag heads, in the order the groups come; tags no group starts with
 * come in a section of their own.
 * @param {{ tag_group: string|null }[]} rows
 * @param {{ name: string, label: string }[]} groups The tag groups, to title each section.
 * @returns {{ key: string, title: string, rows: object[] }[]}
 */
const sectionsOf = (rows, groups) => {
  const sections = new Map();
  rows.forEach((row) => {
    const key = row.tag_group ?? "";
    if (!sections.has(key)) {
      const label = groups.find((group) => group.name === row.tag_group)?.label;
      sections.set(key, { key, title: row.tag_group ? `${label} tags` : UNUSED_SECTION_TITLE, rows: [] });
    }
    sections.get(key).rows.push(row);
  });
  return [...sections.values()];
};

/**
 * An "ESG pillars" button that lists the module tag at the top of each tag group (Module, Emission, Energy tags) with
 * the pillar it sits under, so the super admin can put them under Environment, Social or Governance in one place. The
 * tags below a module tag follow it. A tag's pillar applies to every KPI it tags, in every group it heads. The listing
 * loads each time the panel opens.
 *
 * @param {object} props
 * @param {number} props.unassignedCount module tags with no pillar yet
 * @param {{ tag_group: string|null, keyword_value: string, esg_pillar: string|null, kpi_count: number }[]} props.rows
 * @param {{ name: string, label: string }[]} props.groups The tag groups, to title the sections.
 * @param {boolean} props.loading
 * @param {string} props.error
 * @param {() => void} props.onLoad
 * @param {(moduleTag: string, esgPillar: string) => Promise<string|null>} props.onSetPillar resolves to why it failed, or null
 */
export default function EsgPillarsMenu({ unassignedCount, rows, groups, loading, error, onLoad, onSetPillar }) {
  const [open, setOpen] = useState(false);
  const [onlyUnset, setOnlyUnset] = useState(false);
  const [savingTags, setSavingTags] = useState(() => new Set());
  const [failure, setFailure] = useState(null);
  const rootRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  const shown = useMemo(() => (onlyUnset ? rows.filter((row) => !row.esg_pillar) : rows), [rows, onlyUnset]);
  const sections = useMemo(() => sectionsOf(shown, groups), [shown, groups]);

  const toggle = () => {
    if (!open) {
      setFailure(null);
      onLoad();
    }
    setOpen(!open);
  };

  const save = async (moduleTag, esgPillar) => {
    const tag = normalizeTagText(moduleTag);
    setSavingTags((current) => new Set(current).add(tag));
    setFailure(null);
    const message = await onSetPillar(moduleTag, esgPillar);
    setSavingTags((current) => {
      const next = new Set(current);
      next.delete(tag);
      return next;
    });
    if (message) setFailure({ moduleTag, message });
  };

  return (
    <div className="kgb-colpicker" ref={rootRef}>
      <button type="button" className="kgb-btn" aria-expanded={open} onClick={toggle}>
        ESG pillars
        {unassignedCount > 0 ? <span className="kgb-esg-count">{unassignedCount} not set</span> : null}
      </button>
      {open ? (
        <AnchoredPanel anchorRef={rootRef} onClose={close} align="right" className="kgb-picker kgb-esg-panel">
          <p className="kgb-picker-title">ESG pillar of each module tag</p>
          <label className="kgb-colpicker-option">
            <input
              type="checkbox"
              className="kgb-checkbox"
              checked={onlyUnset}
              onChange={(e) => setOnlyUnset(e.target.checked)}
            />
            Only tags without a pillar
          </label>
          {error ? (
            <div className="kgb-esg-error" role="alert">
              {error}{" "}
              <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onLoad}>
                Retry
              </button>
            </div>
          ) : null}
          {loading && rows.length === 0 ? <p className="kgb-picker-empty">Loading module tags…</p> : null}
          {!loading && !error && shown.length === 0 ? (
            <p className="kgb-picker-empty">{onlyUnset ? "Every module tag has a pillar" : "No module tags yet"}</p>
          ) : null}
          {sections.map((section) => (
            <section key={section.key} className="kgb-esg-section">
              <p className="kgb-esg-group">{section.title}</p>
              <ul className="kgb-esg-list">
                {section.rows.map((row) => (
                  <li key={normalizeTagText(row.keyword_value)} className="kgb-esg-row">
                    <span className="kgb-esg-tag">{row.keyword_value}</span>
                    <span className="kgb-esg-kpis">
                      {row.kpi_count} {row.kpi_count === 1 ? "KPI" : "KPIs"}
                    </span>
                    <select
                      className="kgb-esg-select"
                      aria-label={`ESG pillar of ${row.keyword_value} in ${section.title}`}
                      value={row.esg_pillar || ""}
                      disabled={loading || savingTags.has(normalizeTagText(row.keyword_value))}
                      onChange={(e) => save(row.keyword_value, e.target.value)}
                    >
                      <option value="" disabled>
                        Not set
                      </option>
                      {ESG_PILLARS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {failure && failure.moduleTag === row.keyword_value ? (
                      <span className="kgb-esg-error" role="alert">
                        {failure.message}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <p className="kgb-esg-note">
            A module tag's pillar applies to every KPI whose tags start with it, in every group it heads; the tags below
            it follow.
          </p>
        </AnchoredPanel>
      ) : null}
    </div>
  );
}
